import React, { FC } from "react";
import { Avatar, Box, Stack, SxProps, Typography } from "@mui/material";
import { Link } from "gatsby-theme-material-ui";

import { Image } from "@src/types";
import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors";
import { ArrowBackOutlined } from "@mui/icons-material";
import ButtonPicker from "../Buttons/picker";

interface AuthorProps {
  name: string;
  title?: string;
  image?: string;
}

interface BackLink {
  label: string;
  url: string;
}

interface Props {
  title: string;
  subtitle?: string;
  category?: string;
  description?: string;
  image?: Image;
  author?: AuthorProps;
  backLink?: BackLink;
  ctaButton?: { type: string; label?: string };
  additionalTitleStyles?: SxProps;
}

const BaseHero: FC<Props> = ({
  title,
  category,
  subtitle,
  description,
  image,
  author,
  backLink,
  ctaButton,
  additionalTitleStyles = {},
}) => {
  return (
    <Stack
      alignItems="center"
      sx={{
        width: "100%",
        marginTop: "-68px",
        marginBottom: "40px",
        paddingTop: "68px",
        backgroundColor: "#F6F9FC",
        overflow: "hidden",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        maxWidth="1080px"
        alignItems="center"
        justifyContent="center"
        textAlign="left"
        gap={5}
        sx={{
          position: "relative",
          width: "100%",
          minHeight: "300px",
          padding: { xs: "16px 24px", lg: "24px 0" },
          zIndex: "2",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-68px",
            right: { xs: "0", md: "-1720px", lg: "-1600px" },
            width: "2782px",
            height: "400px",
            zIndex: "1",
            pointerEvents: "none",
            backgroundImage: {
              xs: "none",
              md: "url(/backgrounds/service-hero-wave.svg)",
            },
            backgroundRepeat: "no-repeat",
          }}
        />
        <Stack
          maxWidth="1080px"
          alignItems="flex-start"
          justifyContent="center"
          direction="column"
          sx={{
            position: "relative",
            width: "100%",
            zIndex: "5",
          }}
        >
          {(backLink || category) && (
            <Stack direction="row" alignItems="center">
              {backLink && (
                <Link href={backLink.url} underline="none">
                  <Typography
                    variant="h6"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      color: Colors.PrimaryMain,
                    }}
                  >
                    <ArrowBackOutlined
                      sx={{ marginRight: 1, fontSize: "18px" }}
                    />{" "}
                    Back to {backLink.label}
                  </Typography>
                </Link>
              )}
              {backLink && category && (
                <Typography
                  variant="h6"
                  sx={{ padding: "0 8px", color: Colors.PrimaryMain }}
                >
                  |
                </Typography>
              )}
              {category && (
                <Typography variant="h6" sx={{ color: Colors.PrimaryMain }}>
                  {category}
                </Typography>
              )}
            </Stack>
          )}
          <Typography
            variant="h1"
            mb="20px"
            mt="0"
            sx={{
              maxWidth: "850px",
              color: Colors.PrimaryMain,
              ...additionalTitleStyles,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="h5"
              mb={2}
              sx={{
                fontSize: "24px",
                lineHeight: "38px",
                fontWeight: "500",
                color: Colors.PrimaryMain,
              }}
            >
              {subtitle}
            </Typography>
          )}
          {description && (
            <Typography
              variant="h6"
              sx={{
                maxWidth: "620px",
                marginBottom: "20px",
                color: subtitle ? Colors.TextPrimary : Colors.PrimaryMain,
              }}
            >
              {description}
            </Typography>
          )}
          {author && (
            <Stack direction="row" alignItems="center">
              {author?.image && (
                <Avatar
                  src={author.image}
                  sx={{
                    height: 60,
                    width: 60,
                    marginRight: "10px",
                  }}
                />
              )}
              <Stack>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: Colors.PrimaryMain,
                  }}
                >
                  {author.name}
                </Typography>
                {author?.title && (
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "14px",
                      lineHeight: "22px",
                      color: "#885F9F",
                    }}
                  >
                    {author.title}
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}
          {ctaButton && (
            <Stack mt={2}>
              <ButtonPicker type={ctaButton.type} label={ctaButton.label} />
            </Stack>
          )}
        </Stack>
        {image && (
          <Stack sx={{ position: "relative", zIndex: "5" }}>
            <Box
              component="img"
              src={image.url}
              width={image.width}
              height={image.height}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default BaseHero;
